import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSession } from '../App.jsx';
import { api } from '../lib/api.js';
import { ago, day, inr, num } from '../lib/format.js';
import { today } from '../lib/dates.js';
import { Alert, Card, CardSkeleton, Empty, Field, StatCard, useAsync } from '../components/ui.jsx';
import ExportButton from '../components/ExportButton.jsx';
import ReminderActions from '../components/ReminderActions.jsx';

/** Who owes what, and for how long — the collections view. */
export default function Receivables() {
  const session = useSession();
  const entityId = session.entities[0] && session.entities[0].id;
  const [asOf, setAsOf] = useState(today());
  const [params] = useSearchParams();
  const [only, setOnly] = useState(() => (['overdue', 'old', 'over_limit'].includes(params.get('only')) ? params.get('only') : ''));
  const [open, setOpen] = useState(null);
  const rep = useAsync(() => api.get(`/api/reports/receivables?entity_id=${entityId}&as_of=${asOf}`), [entityId, asOf]);

  const data = rep.data;
  const rows = data ? data.parties.filter((p) => (
    !only
    || (only === 'overdue' && p.overdue > 0)
    || (only === 'over_limit' && p.over_limit)
    || (only === 'old' && (num(p.d91_180) + num(p.d180p)) > 0)
  )) : [];
  const old = data ? num(data.totals.d91_180) + num(data.totals.d180p) : 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Receivables</h1>
          <div className="page-sub">
            What each customer owes, aged by how long it has been open. The balance is Tally's; the ageing comes from
            Tally's open bills where the Connector sends them.
          </div>
        </div>
        <div className="toolbar" style={{ marginBottom: 0 }}>
          <Field label="As of">
            <input type="date" value={asOf} max={today()} onChange={(e) => e.target.value && setAsOf(e.target.value)} />
          </Field>
          <ExportButton disabled={!data} build={() => ({
            filename: `receivables-${asOf}`,
            title: 'Receivables',
            subtitle: `As of ${day(asOf)} · ${data.source === 'tally_bills' ? "aged from Tally's bills" : 'estimated ageing'}`,
            sheets: [{
              name: 'Receivables',
              columns: [
                { key: 'name', header: 'Customer' }, { key: 'gstin', header: 'GSTIN' },
                { key: 'phone', header: 'Phone' }, { key: 'email', header: 'E-mail' },
                { key: 'outstanding', header: 'Outstanding', type: 'money', total: true },
                ...data.buckets.map((b) => ({ key: b.key, header: b.label, type: 'money', total: true })),
                { key: 'unexplained', header: 'Undated / on account', type: 'money', total: true },
                { key: 'overdue', header: 'Overdue', type: 'money', total: true },
                { key: 'oldest_days', header: 'Oldest bill (days)', type: 'int' },
                { key: 'credit_limit', header: 'Credit limit', type: 'money' },
                { key: 'credit_days', header: 'Credit days', type: 'int' },
              ],
              rows: data.parties,
            }],
          })} />
        </div>
      </div>

      <Alert onRetry={rep.reload}>{rep.error}</Alert>
      {rep.loading && !data && <CardSkeleton lines={5} />}

      {data && (
        <>
          {data.source === 'estimated' && (
            <Alert kind="info">
              {data.note} {data.synced_at ? `Balances read from Tally ${ago(data.synced_at)}.` : 'No balances have been read from Tally yet.'}
            </Alert>
          )}
          <div className="grid kpi" style={{ marginBottom: 16 }}>
            <StatCard label="Outstanding" value={inr(data.totals.outstanding || 0, 0)}
              foot={`${data.parties.filter((p) => p.outstanding > 0).length} customers owe money`} />
            <StatCard label="Overdue" value={inr(data.totals.overdue || 0, 0)}
              foot="past the bill's due date or the credit days"
              tone={num(data.totals.overdue) > 0 ? 'attention' : undefined} onClick={() => setOnly(only === 'overdue' ? '' : 'overdue')} />
            <StatCard label="Over 90 days" value={inr(old, 0)} foot="91 days and older"
              tone={old > 0 ? 'attention' : undefined} onClick={() => setOnly(only === 'old' ? '' : 'old')} />
            <StatCard label="Over the limit" value={data.parties.filter((p) => p.over_limit).length}
              foot="customers past their credit limit"
              tone={data.parties.some((p) => p.over_limit) ? 'attention' : undefined}
              onClick={() => setOnly(only === 'over_limit' ? '' : 'over_limit')} />
          </div>

          <div className="segmented" style={{ marginBottom: 14 }} role="group" aria-label="Show">
            {[['', 'All'], ['overdue', 'Overdue'], ['old', 'Over 90 days'], ['over_limit', 'Over the limit']].map(([k, l]) => (
              <button key={l} aria-pressed={only === k} onClick={() => setOnly(k)}>{l}</button>
            ))}
          </div>

          <Card pad={rows.length === 0}>
            {rows.length === 0 ? (
              <Empty title="Nobody in this view">
                {data.parties.length ? 'No customer matches this filter.' : 'Tally reports no money owed — or the outstandings have not been synced yet.'}
              </Empty>
            ) : (
              <div className="table-wrap" style={{ margin: 0, padding: 0 }}>
                <table>
                  <thead>
                    <tr><th>Customer</th><th className="r">Outstanding</th>
                      {data.buckets.map((b) => <th key={b.key} className="r">{b.label}</th>)}
                      <th className="r">Overdue</th><th /></tr>
                  </thead>
                  <tbody>
                    {rows.map((p) => (
                      <React.Fragment key={p.party_id}>
                        <tr className="clickable" onClick={() => setOpen(open === p.party_id ? null : p.party_id)}>
                          <td>{p.name}
                            <div className="sub">
                              {p.credit_limit !== null ? `limit ${inr(p.credit_limit, 0)}` : 'no limit'}
                              {p.credit_days !== null && p.credit_days !== undefined ? ` · ${p.credit_days} days` : ''}
                              {p.over_limit && <span style={{ color: 'var(--critical-text)' }}> · over the limit</span>}
                              {p.on_hold && <span style={{ color: 'var(--critical-text)' }}> · on hold</span>}
                            </div>
                          </td>
                          <td className="r num"><b>{inr(p.outstanding, 0)}</b></td>
                          {data.buckets.map((b, bi) => (
                            <td key={b.key} className="r num" style={{ color: num(p[b.key]) && bi >= 2 ? 'var(--critical-text)' : undefined }}>
                              {num(p[b.key]) ? inr(p[b.key], 0) : '—'}
                            </td>
                          ))}
                          <td className="r num" style={{ color: p.overdue > 0 ? 'var(--critical-text)' : undefined }}>
                            {p.overdue ? inr(p.overdue, 0) : '—'}
                          </td>
                          <td className="r muted" aria-hidden="true">{open === p.party_id ? '▴' : '▾'}</td>
                        </tr>
                        {open === p.party_id && (
                          <tr>
                            <td colSpan={data.buckets.length + 4} style={{ background: 'var(--surface-2)' }}>
                              <PartyDetail party={p} source={data.source} entityId={entityId} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><th className="r">Total</th><th className="r num">{inr(data.totals.outstanding || 0, 0)}</th>
                      {data.buckets.map((b) => <th key={b.key} className="r num">{inr(data.totals[b.key] || 0, 0)}</th>)}
                      <th className="r num">{inr(data.totals.overdue || 0, 0)}</th><th /></tr>
                  </tfoot>
                </table>
              </div>
            )}
            {num(data.totals.unexplained) !== 0 && (
              <div className="card-note" style={{ marginTop: 10 }}>
                {inr(data.totals.unexplained, 0)} of the balance has no bill date (on account, an opening balance, or bills
                InvIT did not raise) and is not in an age column.
              </div>
            )}
          </Card>
        </>
      )}
    </>
  );
}

function PartyDetail({ party, source, entityId }) {
  const bills = useAsync(
    () => (source === 'tally_bills' ? api.get(`/api/reports/receivables/${party.party_id}/bills`) : Promise.resolve(null)),
    [party.party_id, source]);
  return (
    <div style={{ padding: '6px 4px' }}>
      <ReminderActions party={party} bills={bills.data ? bills.data.bills : null} entityId={entityId} />
      {source === 'tally_bills' && bills.data && (
        bills.data.bills.length === 0 ? <div className="sub" style={{ marginTop: 8 }}>No open bills.</div> : (
          <table style={{ marginTop: 10 }}>
            <thead><tr><th>Bill</th><th>Date</th><th>Due</th><th className="r">Age</th><th className="r">Open</th></tr></thead>
            <tbody>
              {bills.data.bills.map((b, i) => (
                <tr key={`${b.bill_ref}-${i}`}>
                  <td className="mono">{b.bill_ref}</td>
                  <td>{b.bill_date ? day(b.bill_date) : '—'}</td>
                  <td>{b.due_date ? day(b.due_date) : '—'}</td>
                  <td className="r num">{b.age_days ?? '—'}</td>
                  <td className="r num">{inr(b.amount, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}
